import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, ArrowRight, Coffee, Sparkles, Calendar as CalendarIcon, CheckSquare, Video, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import UserAvatar from '@/components/common/UserAvatar';
import type { UnifiedEvent } from './types';

interface Props {
  events: UnifiedEvent[];
  selectedDate: Date;
  onEventClick: (e: UnifiedEvent) => void;
}

function fmtCountdown(ms: number): string {
  if (ms <= 0) return 'ora';
  const totalMin = Math.floor(ms / 60000);
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function sourceIcon(s: UnifiedEvent['source']) {
  if (s === 'appointment') return CalendarIcon;
  if (s === 'task') return CheckSquare;
  if (s === 'videocall') return Video;
  return Users;
}

export default function CockpitNowNextCards({ events, selectedDate, onEventClick }: Props) {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const isToday = useMemo(() => {
    const d = new Date(selectedDate);
    const t = new Date();
    return d.toDateString() === t.toDateString();
  }, [selectedDate]);

  const { current, next } = useMemo(() => {
    const timed = events.filter(e => !e.all_day && e.start_time_iso);
    const sorted = [...timed].sort((a, b) =>
      new Date(a.start_time_iso).getTime() - new Date(b.start_time_iso).getTime()
    );
    const cur = sorted.find(e => {
      const s = new Date(e.start_time_iso).getTime();
      const en = e.end_time_iso ? new Date(e.end_time_iso).getTime() : s + 30 * 60_000;
      return s <= now && en > now;
    }) || null;
    const nxt = sorted.find(e => new Date(e.start_time_iso).getTime() > now) || null;
    return { current: cur, next: nxt };
  }, [events, now]);

  const renderCard = (
    label: string,
    evt: UnifiedEvent | null,
    accent: 'now' | 'next',
  ) => {
    const Empty = (
      <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
        <div className={cn(
          'w-10 h-10 rounded-full flex items-center justify-center',
          accent === 'now' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600' : 'bg-blue-100 dark:bg-blue-900/30 text-blue-600',
        )}>
          {accent === 'now' ? <Coffee className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-gray-400">{label}</div>
          <div className="text-sm font-medium">
            {accent === 'now' ? 'Nessun evento in corso' : isToday ? 'Niente in arrivo' : 'Nessun prossimo evento'}
          </div>
        </div>
      </div>
    );

    if (!evt) {
      return (
        <motion.div
          key={`empty-${accent}`}
          layout
          className={cn(
            'relative rounded-2xl border bg-white dark:bg-gray-900 p-4 shadow-sm overflow-hidden',
            'border-gray-200 dark:border-gray-700',
          )}
        >
          {Empty}
        </motion.div>
      );
    }

    const startMs = new Date(evt.start_time_iso).getTime();
    const endMs = evt.end_time_iso ? new Date(evt.end_time_iso).getTime() : startMs + 30 * 60_000;
    const diff = accent === 'now' ? endMs - now : startMs - now;
    const Icon = sourceIcon(evt.source);
    const customer = evt.metadata?.customerName || evt.metadata?.creatorName || evt.metadata?.workgroupName;

    return (
      <motion.div
        key={`${accent}-${evt.id}`}
        layout
        whileHover={{ y: -2 }}
        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        role="button"
        tabIndex={0}
        aria-label={`${label}: ${evt.title}`}
        onClick={() => onEventClick(evt)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onEventClick(evt);
          }
        }}
        className={cn(
          'relative rounded-2xl border bg-white dark:bg-gray-900 p-4 shadow-sm overflow-hidden cursor-pointer',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900',
          accent === 'now'
            ? 'border-emerald-300 dark:border-emerald-700'
            : 'border-blue-200 dark:border-blue-800',
        )}
      >
        {accent === 'now' && (
          <motion.div
            className="absolute inset-0 rounded-2xl pointer-events-none"
            style={{ boxShadow: '0 0 0 0 rgba(16,185,129,0.45)' }}
            animate={{
              boxShadow: [
                '0 0 0 0 rgba(16,185,129,0.45)',
                '0 0 0 6px rgba(16,185,129,0)',
                '0 0 0 0 rgba(16,185,129,0)',
              ],
            }}
            transition={{ duration: 2.4, repeat: Infinity }}
          />
        )}
        <div className="flex items-center justify-between mb-2">
          <span className={cn(
            'text-[10px] uppercase tracking-widest font-bold',
            accent === 'now' ? 'text-emerald-600 dark:text-emerald-400' : 'text-blue-600 dark:text-blue-400',
          )}>
            {label}
          </span>
          <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400">
            <Clock className="w-3 h-3" />
            {accent === 'now' ? `finisce tra ${fmtCountdown(diff)}` : `tra ${fmtCountdown(diff)}`}
          </span>
        </div>
        <div className="flex items-start gap-3">
          <div
            className="w-1 self-stretch rounded-full"
            style={{ backgroundColor: evt.type_color || '#6366f1' }}
          />
          <UserAvatar
            id={evt.assigned_to_id || undefined}
            name={evt.assigned_to_name || undefined}
            avatarUrl={evt.assigned_to_avatar_url || undefined}
            size="md"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
              <Icon className="w-3 h-3" style={{ color: evt.type_color }} />
              <span>{evt.start_time}{evt.end_time ? ` – ${evt.end_time}` : ''}</span>
            </div>
            <div className="text-base font-semibold text-gray-900 dark:text-white truncate">{evt.title}</div>
            {customer && (
              <div className="text-sm text-gray-600 dark:text-gray-300 truncate">{customer}</div>
            )}
          </div>
          <ArrowRight className="w-4 h-4 text-gray-300 dark:text-gray-600 self-center" />
        </div>
      </motion.div>
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <AnimatePresence mode="popLayout">
        {renderCard('Adesso', isToday ? current : null, 'now')}
        {renderCard('Prossimo', isToday ? next : (events.find(e => !e.all_day) || null), 'next')}
      </AnimatePresence>
    </div>
  );
}
